import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InsightsIcon from "@mui/icons-material/Insights";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const catalogSectionSx = {
  scrollSnapAlign: "start",
  minHeight: "calc(100svh - 44px)",
  px: { xs: 3, sm: 4 },
  py: { xs: 5, sm: 7 },
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  bgcolor: "#fff",
} as const;

const sectionTitleSx = {
  mt: 1,
  fontWeight: 800,
  letterSpacing: "-0.055em",
  fontSize: { xs: "2rem", sm: "2.35rem" },
  lineHeight: 1.16,
} as const;

const lookinbodyWebViews = [
  {
    id: "analysis",
    label: "분석",
    title: "체성분 분석",
    description: "측정 결과에서 근육과 체지방 관련 항목을 확인합니다.",
    image: "/images/inbody/result-sheet-overview.png",
    alt: "여러 체성분 지표가 표시된 InBody 결과지 예시",
  },
  {
    id: "tracking",
    label: "추적",
    title: "측정 결과 추적",
    description: "날짜별 결과를 비교해 체지방률과 골격근량의 변화를 확인합니다.",
    image: "/images/inbody/lookinbody-web-tracking.png",
    alt: "LookinBody Web의 측정 기록 추적 화면 예시",
  },
  {
    id: "report",
    label: "리포트",
    title: "측정 리포트",
    description: "측정 결과와 리포트를 한 화면에서 확인합니다.",
    image: "/images/inbody/lookinbody-web-sync.png",
    alt: "LookinBody Web의 결과 리포트 화면 예시",
  },
] as const;

const whyMeasureViews = [
  {
    number: "01",
    title: "부위별 체성분",
    description: "몸통·팔·다리의 근육과 체지방 관련 결과를 확인합니다.",
    image: "/images/inbody/why-measure-analysis.png",
    alt: "체성분 균형을 확인하는 InBody 앱 화면",
  },
  {
    number: "02",
    title: "항목별 변화",
    description: "골격근량, 체지방률, BMI 등의 결과를 날짜별로 비교합니다.",
    image: "/images/inbody/why-measure-tracking.png",
    alt: "측정 항목별 변화를 확인하는 InBody 앱 화면",
  },
  {
    number: "03",
    title: "식단 가이드",
    description: "측정 결과와 목표에 따른 식단 가이드 예시를 확인합니다.",
    image: "/images/inbody/why-measure-guide.png",
    alt: "목표에 맞춘 식단 가이드를 확인하는 InBody 앱 화면",
  },
] as const;

const InBodyCatalog = () => {
  const navigate = useNavigate();
  const [activeLookinbodyView, setActiveLookinbodyView] = useState<(typeof lookinbodyWebViews)[number]["id"]>("analysis");
  const activeView = lookinbodyWebViews.find((view) => view.id === activeLookinbodyView) ?? lookinbodyWebViews[0];

  return (
    <Box
      component="main"
      aria-label="InBody 모바일 카탈로그"
      sx={{
        mx: "auto",
        maxWidth: 560,
        height: "calc(100svh - 44px)",
        overflowY: "auto",
        scrollSnapType: "y mandatory",
        scrollBehavior: "smooth",
        color: "#183d35",
        bgcolor: "#fff",
        "&::-webkit-scrollbar": { display: "none" },
        "@keyframes inBodyCatalogHeroZoom": {
          from: { opacity: 0.4, transform: "scale(1.06)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
        "@keyframes inBodyCatalogHeroCopy": {
          from: { opacity: 0, transform: "translateY(18px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes inBodyCatalogScrollReveal": {
          from: { opacity: 0, transform: "translateY(44px) scale(0.985)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "@supports (animation-timeline: view())": {
          "& .inbody-scroll-reveal": {
            animation: "inBodyCatalogScrollReveal linear both",
            animationTimeline: "view(block)",
            animationRange: "entry 12% cover 38%",
          },
        },
        "@media (prefers-reduced-motion: reduce)": {
          scrollBehavior: "auto",
          "& *": { animation: "none !important", transition: "none !important" },
        },
      }}
    >
      <Box
        component="section"
        data-testid="inbody-fullscreen-hero"
        data-scroll-animated="true"
        sx={{
          scrollSnapAlign: "start",
          minHeight: "calc(100svh - 44px)",
          height: "calc(100svh - 44px)",
          position: "relative",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Box
          component="img"
          src="/images/inbody/installed-analyzer.png"
          alt="커뮤니티 센터에 설치된 InBody"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            animation: "inBodyCatalogHeroZoom 1.2s cubic-bezier(0.2, 0.72, 0.2, 1) both",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.74) 27%, rgba(255,255,255,0.04) 54%, rgba(255,255,255,0.1) 100%)",
          }}
        />
        <Button
          onClick={() => navigate(-1)}
          aria-label="이전 화면으로 돌아가기"
          startIcon={<ArrowBackIcon />}
          sx={{ position: "absolute", zIndex: 1, top: 24, left: { xs: 24, sm: 32 }, color: "#315b51", px: 0 }}
        >
          돌아가기
        </Button>
        <Stack
          spacing={2.25}
          sx={{
            position: "absolute",
            zIndex: 1,
            top: { xs: "17%", sm: "15%" },
            left: { xs: 24, sm: 32 },
            right: { xs: 24, sm: 32 },
            animation: "inBodyCatalogHeroCopy 700ms 160ms cubic-bezier(0.2, 0.72, 0.2, 1) both",
          }}
        >
          <Chip label="디에트르 라포레 COMMUNITY FITNESS" size="small" sx={{ alignSelf: "flex-start", bgcolor: "rgba(224,248,241,0.88)", color: "#176651", backdropFilter: "blur(8px)", fontWeight: 800 }} />
          <Typography component="h1" variant="h2" sx={{ fontWeight: 800, letterSpacing: "-0.075em", fontSize: { xs: "4.55rem", sm: "5.7rem" }, lineHeight: 0.9 }}>
            InBody
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 750, lineHeight: 1.1, letterSpacing: "-0.055em", fontSize: { xs: "2rem", sm: "2.45rem" } }}>
            체성분 측정
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 305, color: "#35564d", lineHeight: 1.65, fontWeight: 500 }}>
            체중, 골격근량, 체지방량 등 몸을 구성하는 항목을 측정합니다.
          </Typography>
        </Stack>
        <Typography variant="caption" sx={{ position: "absolute", zIndex: 1, left: 0, right: 0, bottom: 24, textAlign: "center", color: "#315b51", fontWeight: 700, letterSpacing: "0.03em" }}>
          측정 항목과 사용 방법 보기
        </Typography>
      </Box>

      <Box component="section" className="inbody-scroll-reveal" data-testid="inbody-scroll-reveal" sx={catalogSectionSx}>
        <Typography variant="overline" sx={{ color: "#168970", fontWeight: 800, letterSpacing: "0.12em" }}>
          WHAT IS INBODY
        </Typography>
        <Typography component="h2" variant="h3" sx={sectionTitleSx}>
          체성분을 확인합니다
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, color: "#46605a", lineHeight: 1.7 }}>
          체중뿐 아니라 골격근량, 체지방량, 체지방률, 부위별 균형을 함께 확인합니다.
        </Typography>
        <Box sx={{ mt: 3, mx: { xs: -3, sm: -4 } }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              px: { xs: 3, sm: 4 },
              pb: 1.5,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {whyMeasureViews.map((view) => (
              <Box
                key={view.number}
                sx={{
                  flex: "0 0 min(78vw, 330px)",
                  scrollSnapAlign: "center",
                  overflow: "hidden",
                  borderRadius: 5,
                  bgcolor: "#f4fbf8",
                  border: "1px solid #e0f0ea",
                  boxShadow: "0 14px 34px rgba(35, 98, 81, 0.1)",
                }}
              >
                <Box
                  component="img"
                  src={view.image}
                  alt={view.alt}
                  sx={{ display: "block", width: "100%", aspectRatio: "672 / 1468", objectFit: "cover" }}
                />
                <Box sx={{ p: 2 }}>
                  <Typography variant="overline" sx={{ color: "#168970", fontWeight: 800, letterSpacing: "0.1em" }}>{view.number}</Typography>
                  <Typography variant="subtitle1" sx={{ mt: 0.15, fontWeight: 800 }}>{view.title}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: "#527168", lineHeight: 1.55 }}>{view.description}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" sx={{ display: "block", px: { xs: 3, sm: 4 }, color: "#68857c" }}>
            앱 화면을 옆으로 넘겨 확인하세요.
          </Typography>
        </Box>
        <Stack spacing={1.2} sx={{ mt: 4 }}>
          {[
            ["체지방률", "체지방 비율", "체중 변화와 함께 체지방 비율을 확인합니다."],
            ["골격근량", "근육량", "측정일별 골격근량을 비교합니다."],
            ["부위별 균형", "부위별 결과", "몸통·팔·다리의 체성분 결과를 비교합니다."],
          ].map(([title, subtitle, detail]) => (
            <Stack key={title} direction="row" spacing={2} sx={{ p: 2, borderRadius: 4, bgcolor: "#f4fbf8", border: "1px solid #e0f0ea" }}>
              <InsightsIcon sx={{ color: "#18a789", mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3 }}>{title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: "#2e7765" }}>{subtitle}</Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#648078", lineHeight: 1.45 }}>{detail}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box component="section" className="inbody-scroll-reveal" data-testid="inbody-scroll-reveal" sx={catalogSectionSx}>
        <Typography variant="overline" sx={{ color: "#168970", fontWeight: 800, letterSpacing: "0.12em" }}>
          MEASUREMENT GUIDE
        </Typography>
        <Typography component="h2" variant="h3" sx={sectionTitleSx}>
          측정 방법
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, color: "#527168", lineHeight: 1.7 }}>
          같은 조건에서 측정하면 날짜별 결과를 비교하기 쉽습니다.
        </Typography>
        <Stack spacing={1.4} sx={{ mt: 4 }}>
          {[
            ["01", "측정 전", "식사와 운동 직후에는 측정을 피합니다."],
            ["02", "발 전극", "맨발로 발 전극 위에 올라섭니다."],
            ["03", "손 전극", "양손 전극을 잡고 자세를 유지합니다."],
            ["04", "결과 확인", "측정이 끝난 뒤 결과지를 확인합니다."],
          ].map(([number, title, detail]) => (
            <Stack key={number} direction="row" spacing={2} alignItems="flex-start" sx={{ p: 2, borderRadius: 3, bgcolor: "#f4fbf8" }}>
              <Typography variant="subtitle2" sx={{ color: "#168970", fontWeight: 900 }}>{number}</Typography>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.25, color: "#527168", lineHeight: 1.55 }}>{detail}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box component="section" className="inbody-scroll-reveal" data-testid="inbody-scroll-reveal" sx={{ ...catalogSectionSx, position: "relative" }}>
        <Box sx={{ position: "sticky", top: 24 }}>
          <Typography variant="overline" sx={{ color: "#07866c", fontWeight: 800, letterSpacing: "0.12em" }}>
            RESULT HISTORY
          </Typography>
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            측정 결과 비교
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, color: "#45645c", lineHeight: 1.7 }}>
            날짜별 결과를 비교해 체지방률과 골격근량 등 측정 항목의 변화를 확인합니다.
          </Typography>
          <Box sx={{ mt: 3, p: 2.5, borderRadius: 5, bgcolor: "#f1faf7", border: "1px solid #d9eee7" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>측정 기록 예시</Typography>
                <Typography variant="caption" sx={{ color: "#5f8177" }}>체지방률과 골격근량의 변화</Typography>
              </Box>
              <TimelineIcon sx={{ color: "#069b7c" }} />
            </Stack>
            <Box component="svg" viewBox="0 0 320 130" aria-label="체지방률은 낮아지고 골격근량은 높아지는 예시 그래프" sx={{ display: "block", width: "100%", mt: 1.5 }}>
              <line x1="8" x2="312" y1="28" y2="28" stroke="#d8ebe5" />
              <line x1="8" x2="312" y1="66" y2="66" stroke="#d8ebe5" />
              <line x1="8" x2="312" y1="104" y2="104" stroke="#d8ebe5" />
              <polyline points="12,32 86,45 160,62 234,76 308,91" fill="none" stroke="#ee7a67" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="12,98 86,90 160,77 234,65 308,46" fill="none" stroke="#12a785" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="308" cy="91" r="5" fill="#ee7a67" />
              <circle cx="308" cy="46" r="5" fill="#12a785" />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: "#d76655", fontWeight: 700 }}>● 체지방률 28.4% → 26.8%</Typography>
              <Typography variant="caption" sx={{ color: "#07866c", fontWeight: 700 }}>● 골격근량 23.8kg → 24.5kg</Typography>
            </Stack>
          </Box>
          <Box sx={{ mt: 3, p: 2, borderRadius: 5, bgcolor: "#fff", border: "1px solid #d9eee7" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>앱 화면 예시</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "#527168", lineHeight: 1.55 }}>
              분석, 추적, 리포트 화면을 확인하세요.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {lookinbodyWebViews.map((view) => (
                <Button
                  key={view.id}
                  size="small"
                  variant={activeLookinbodyView === view.id ? "contained" : "outlined"}
                  aria-pressed={activeLookinbodyView === view.id}
                  onClick={() => setActiveLookinbodyView(view.id)}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    borderRadius: 99,
                    fontWeight: 800,
                    color: activeLookinbodyView === view.id ? "#fff" : "#267260",
                    bgcolor: activeLookinbodyView === view.id ? "#168970" : "#fff",
                    borderColor: "#b9dcd2",
                    "&:hover": { bgcolor: activeLookinbodyView === view.id ? "#0f775f" : "#f1faf7", borderColor: "#91cabb" },
                  }}
                >
                  {view.label}
                </Button>
              ))}
            </Stack>
            <Box
              sx={{
                mt: 2,
                overflow: "hidden",
                borderRadius: 3,
                border: "1px solid #e0f0ea",
                bgcolor: "#f7fcfa",
                "@keyframes inBodyCatalogReveal": {
                  from: { opacity: 0, transform: "translateY(8px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Box
                component="img"
                key={activeView.id}
                src={activeView.image}
                alt={activeView.alt}
                sx={{
                  display: "block",
                  width: "100%",
                  aspectRatio: "1.6 / 1",
                  objectFit: "cover",
                  objectPosition: activeView.id === "tracking" ? "center" : "center top",
                  animation: "inBodyCatalogReveal 300ms ease-out",
                }}
              />
              <Box sx={{ p: 1.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{activeView.title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: "#527168", lineHeight: 1.55 }}>{activeView.description}</Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#6e8780", lineHeight: 1.5 }}>
              LookinBody Web 공식 화면 예시입니다. 센터의 실제 자동 연동 및 제공 기능은 현장 설정에 따라 달라질 수 있습니다.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box component="section" className="inbody-scroll-reveal" data-testid="inbody-scroll-reveal" sx={{ ...catalogSectionSx, textAlign: "center", alignItems: "center" }}>
        <MonitorHeartOutlinedIcon sx={{ fontSize: 56, color: "#168970" }} />
        <Typography component="h2" variant="h3" sx={{ ...sectionTitleSx, mt: 2 }}>
          앱으로 결과 관리
        </Typography>
        <Stack spacing={1} sx={{ mt: 3, textAlign: "left" }}>
          {["측정 결과 확인", "날짜별 결과 비교", "식단·운동 가이드 확인"].map((text) => (
            <Stack key={text} direction="row" spacing={1} alignItems="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#168970" }} />
              <Typography variant="body2" sx={{ color: "#527168" }}>{text}</Typography>
            </Stack>
          ))}
        </Stack>
        <Box sx={{ width: "100%", mt: 4, p: 2, borderRadius: 5, bgcolor: "#f4fbf8", border: "1px solid #d9eee7", textAlign: "left" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src="/images/inbody/inbody-app-google-play.jpg"
              alt="InBody 앱의 Google Play 화면 예시"
              sx={{ width: 92, height: 154, objectFit: "cover", objectPosition: "center", borderRadius: 3, boxShadow: "0 10px 24px rgba(35, 98, 81, 0.16)" }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ color: "#168970", fontWeight: 800, letterSpacing: "0.1em" }}>INBODY APP</Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 800, lineHeight: 1.25 }}>InBody 앱 설치</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "#527168", lineHeight: 1.55 }}>
                측정 결과를 앱에서 확인하고 기록을 관리하세요.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.inbody2014.inbody&hl=ko"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Play에서 InBody 앱 설치하기"
                  variant="contained"
                  size="small"
                  sx={{ borderRadius: 99, bgcolor: "#168970", fontWeight: 800, "&:hover": { bgcolor: "#0f775f" } }}
                >
                  Google Play에서 설치
                </Button>
                <Button
                  component="a"
                  href="https://apps.apple.com/kr/app/inbody/id884923678"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="App Store에서 InBody 앱 설치하기"
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 99, borderColor: "#168970", color: "#168970", fontWeight: 800, "&:hover": { borderColor: "#0f775f", bgcolor: "rgba(22, 137, 112, 0.08)" } }}
                >
                  App Store에서 설치
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: "#527168" }}>
          Google Play 또는 App Store에서 InBody를 설치하세요.
        </Typography>
      </Box>
    </Box>
  );
};

export default InBodyCatalog;
